import React from 'react';
import { View, useWindowDimensions } from 'react-native';

import {
  useNavigationBuilder,
  TabRouter,
  StackRouter,
  createNavigatorFactory,
} from '@react-navigation/native';

import { Ionicons } from '@expo/vector-icons';

import tw from '../styles/tailwind';
import Text from '../components/Text';

type LayoutMode = 'master-detail' | 'fullscreen';

// Ancho de ventana mínimo para la tercera columna. Por debajo, el detalle reemplaza a la
// lista en la segunda columna (el validador ya se acomoda en vertical si le queda angosta).
const SIDE_COLUMN_MIN_WINDOW_WIDTH = 1450;


/**
 * StackRouter + tercera columna. Al ir atrás desde una pantalla que no es la de arriba
 * (ej. el back de la cuota con un pago abierto en la tercera columna), el StackRouter cierra
 * la de arriba (o, con target, solo la que pidió atrás y deja huérfana la de encima). Si la
 * pantalla es host de tercera columna (`sideDetail`) y encima tiene abierta esa ruta, se
 * cierran las dos.
 */
function AdminStackRouter(routerOptions: any) {
  const router = StackRouter(routerOptions);
  const sideHosts: Record<string, string> = routerOptions.sideHosts ?? {};

  return {
    ...router,
    getStateForAction(state: any, action: any, options: any) {
      // target puede venir vacío (el back de una pantalla solo trae source); en ese caso el
      // StackRouter ignora source y cierra la de arriba, que es justo la tercera columna.
      if ((action.type === 'GO_BACK' || action.type === 'POP') && action.source && (action.target == null || action.target === state.key)) {
        const index = state.routes.findIndex((r: any) => r.key === action.source);
        const host = state.routes[index];
        const above = state.routes[index + 1];
        if (index > 0 && host && sideHosts[host.name] && above?.name === sideHosts[host.name]) {
          const routes = state.routes.slice(0, index).concat(state.routes.slice(index + 2));
          return { ...state, index: routes.length - 1, routes };
        }
      }
      return router.getStateForAction(state, action, options);
    },
  };
}

function AdminWebNavigator({
  initialRouteName,
  children,
  screenOptions,
}: any) {
  // host → ruta de su tercera columna, leído de las options de cada Screen.
  const sideHosts = React.useMemo(() => {
    const map: Record<string, string> = {};
    React.Children.forEach(children, (child: any) => {
      const side = child?.props?.options?.sideDetail;
      if (side) map[child.props.name] = side;
    });
    return map;
  }, [children]);

  const { state, navigation, descriptors, NavigationContent } =
    useNavigationBuilder(AdminStackRouter, {
      children,
      screenOptions,
      initialRouteName,
      sideHosts,
    } as any);

  console.log('%c AdminWebNavigator', 'color: pink', state, descriptors);

  return (
    <NavigationContent>
      <View style={tw`flex flex-row flex-1`}>
        {/* Sidebar (100% alto, izquierda) */}
        {screenOptions.sideBar?.({
          state,
          descriptors,
          navigation,
        })}

        {/* Columna derecha: TopBar arriba + content */}
        <View style={tw`flex-1 flex-col`}>
          {screenOptions.topBar?.({
            state,
            descriptors,
            navigation,
          })}

          <AdminLayout
            routes={state.routes}
            descriptors={descriptors}
          />
        </View>
      </View>
    </NavigationContent>
  );
}

export const createMyAdminNavigator = createNavigatorFactory(AdminWebNavigator);


function WebChildrenNavigator({
  initialRouteName,
  children,
  screenOptions,
  contentStyle,
}: any) {
  const { state, navigation, descriptors, NavigationContent } =
    useNavigationBuilder(TabRouter, {
      children,
      screenOptions,
      initialRouteName,
    });

  console.log('%c WebChildrenNavigator', 'color: yellow', state, descriptors);

  return (
    <NavigationContent>
      <View style={tw.style(`flex-1`, contentStyle)}>
        {state.routes.map((route, index) => {
          const isFocused = state.index === index;

          if (!isFocused) return null;

          return (
            <View
              key={route.key}
              style={tw.style(`flex-1 h-100%`)}
            >
              {descriptors[route.key].render()}
            </View>
          );
        })}
      </View>
    </NavigationContent>
  );
}

export const createChildrenNavigator = createNavigatorFactory(WebChildrenNavigator);


function AdminLayout({ routes, descriptors }: any) {
  const hasChildren = routes.length > 1;

  // El screen "AdminTabStack" (outer route 0) recibe en sus options el mapa
  // `innerLayouts` (derivado de DeepStackScreens en AdminStack.web.tsx).
  // De ahí leemos qué layout pide la screen focused del inner TabRouter.
  const adminTabStackOptions: any = descriptors[routes[0].key]?.options ?? {};
  const innerLayouts: Record<string, LayoutMode> = adminTabStackOptions.innerLayouts ?? {};

  const innerState = routes[0]?.state;
  // En el primer render el navegador interno aún no reporta su estado, así que caemos
  // a su ruta inicial (initialInnerRoute) para detectar el layout correcto desde el inicio.
  const focusedInnerRouteName: string | undefined =
    innerState?.routes?.[innerState.index]?.name ?? adminTabStackOptions.initialInnerRoute;
  const innerLayout: LayoutMode =
    (focusedInnerRouteName && innerLayouts[focusedInnerRouteName]) || 'master-detail';

  // Fullscreen solo aplica cuando no hay details apilados.
  // Si hay un detail sobre una pantalla fullscreen (ej. la configuración que abre el dashboard
  // de Autorizaciones), el dashboard conserva el espacio y el detail se acopla a la derecha
  // con ancho de teléfono, en vez de encoger el dashboard a 400px.
  const isFullscreen = innerLayout === 'fullscreen' && !hasChildren;
  const isWideMaster = innerLayout === 'fullscreen';
  const narrowDetailWidth = 480;

  console.log(
    '%c AdminLayout render',
    'color: lime',
    'routes:', routes.length,
    'hasChildren:', hasChildren,
    'innerFocused:', focusedInnerRouteName,
    'innerLayout:', innerLayout,
    'isFullscreen:', isFullscreen,
  );

  // Importante: NO usar un return separado para fullscreen.
  // Cambiar la estructura DOM desmonta el inner navigator → pierde su state y vuelve a initialRouteName.
  // Mantener la misma estructura, solo variar anchos/visibilidad.

  // Master column width:
  // - fullscreen: 100%
  // - fullscreen con detail abierto: lo que deja el detail de ancho teléfono (+16px del ml-4)
  // - master-detail con detail abierto: 400px, o `masterWidth` si el detail lo pide
  // - master-detail solo: 100%
  // Un detail puede pedir en sus options `masterWidth` (master más delgado) y
  // `detailMaxWidth` (no estirarse más de eso), como las dos columnas de Accesos.
  const optionsOf = (route: any) => descriptors[route.key]?.options ?? {};
  const topOptions = hasChildren ? optionsOf(routes[routes.length - 1]) : {};
  const masterColumnWidth = topOptions.masterWidth ?? 400;
  const masterWidth = isWideMaster
    ? (hasChildren ? `calc(100% - ${narrowDetailWidth + 16}px)` : '100%')
    : (hasChildren ? masterColumnWidth : '100%');

  // Mostramos SOLO el detalle más reciente (tope del stack) como única columna derecha.
  // Así abrir una nueva pantalla reemplaza la acoplada en vez de apilar más columnas;
  // "atrás" (pop) vuelve a mostrar la anterior.
  let detailRoute = hasChildren ? routes[routes.length - 1] : null;

  // Tercera columna: un detail con `sideDetail: '<ruta>'` en sus options (ej. la lista de
  // comprobantes) reserva desde que se abre una columna a su derecha para esa ruta (el
  // comprobante). Mientras no se elige nada muestra `sideDetailEmpty`; al elegir, la ruta
  // se apila encima pero se pinta en la tercera columna y la lista se queda en la segunda.
  // En ventanas angostas no hay tercera columna: el detalle reemplaza a la lista en la
  // segunda (comportamiento normal del stack) y "atrás" regresa a la lista.
  const { width: windowWidth } = useWindowDimensions();
  const allowSideColumn = windowWidth >= SIDE_COLUMN_MIN_WINDOW_WIDTH;
  let sideRoute: any = null;
  let sideHost: any = null;
  if (allowSideColumn && detailRoute && optionsOf(detailRoute).sideDetail) {
    sideHost = detailRoute;
  } else if (allowSideColumn && routes.length > 2 && optionsOf(routes[routes.length - 2]).sideDetail === detailRoute?.name) {
    sideHost = routes[routes.length - 2];
    sideRoute = detailRoute;
    detailRoute = sideHost;
  }
  const hasSideColumn = !!sideHost;
  // Ancho de la columna central con tercera columna: 420 fijo, o `sideHostWidth` en las
  // options del host (ej. detalle de cuota, más ancho); ese se encoge hasta 420 si la
  // ventana no da, para que la tercera columna conserve al menos 360.
  const sideHostWidth = hasSideColumn ? optionsOf(sideHost).sideHostWidth : undefined;
  const sideColumnStyle = {
    flexBasis: sideHostWidth ?? 420,
    flexGrow: 0,
    flexShrink: sideHostWidth ? 1 : 0,
    minWidth: 420,
  };

  return (
    <View style={tw`flex flex-row flex-1 bg-background p-4`}>
      <div
        style={tw.style(
          `flex justify-start`,
          { transition: `0.5s ease` },
          { width: masterWidth }
        )}
      >
        <div
          className='SceneContainer'
          style={tw.style(
            `flex rounded-lg h-100%`,
            { overflow: 'auto' },
            isWideMaster ? `flex-1 w-100%` : { width: hasChildren ? masterColumnWidth : 400 }
          )}
        >
          {descriptors[routes[0].key].render()}
        </div>
      </div>

      <div
        className='SceneContainer'
        style={tw.style(
          `flex-1 rounded-lg ml-4`, { overflow: 'auto' },
          { transition: `0.5s ease` },
          !hasChildren && `w-0 ml-0`,
          { flexGrow: hasChildren ? 1 : 0 },
          hasChildren && isWideMaster && { flexBasis: narrowDetailWidth, flexGrow: 0, flexShrink: 0 },
          hasSideColumn && sideColumnStyle,
          hasChildren && !!topOptions.detailMaxWidth && { maxWidth: topOptions.detailMaxWidth }
        )}
      >
        {detailRoute && (
          <React.Fragment key={detailRoute.key}>{descriptors[detailRoute.key].render()}</React.Fragment>
        )}
      </div>

      {/* Tercera columna: sin transición, el contenido aparece directo al elegir. */}
      {hasSideColumn && (
        <div
          className='SceneContainer'
          // flexDirection column: en un div el flex es en fila por defecto, y hay pantallas que
          // devuelven header + contenido sueltos (fragment), que quedarían lado a lado.
          style={tw.style(`flex flex-1 rounded-lg ml-4`, { flexDirection: 'column', overflow: 'auto', minWidth: 360 })}
        >
          {sideRoute ? (
            <React.Fragment key={sideRoute.key}>{descriptors[sideRoute.key].render()}</React.Fragment>
          ) : (
            <View style={tw`flex-1 bg-white rounded-2xl border border-gray-200 items-center justify-center px-8`}>
              <Ionicons name="document-text-outline" size={36} color={tw.color('gray-300')} />
              <Text style={tw`text-gray-400 text-center mt-3`}>
                {optionsOf(sideHost).sideDetailEmpty ?? 'Selecciona un elemento para ver el detalle'}
              </Text>
            </View>
          )}
        </div>
      )}
    </View>
  );
}
